/**
 * Demo environment admin client.
 *
 * `resetDemoEnvironment()` is the one-click reset for the Vitalora demo
 * tenant — requires MG role + the caller to already be on the demo tenant.
 *
 * `bootstrapDemoEnvironment()` is the chicken-and-egg solver: it seeds
 * Vitalora on a stage where the migrator has not been invoked. Requires
 * MG role but NOT that the caller is on the demo tenant. Idempotent.
 *
 * `updateDemoBrandName()` rebrands the demo tenant's visible display
 * name (e.g. flip "Vitalora" to "Nintendo" before a customer meeting).
 * Demo-only on the backend; passing an empty string resets back to the
 * seeded default.
 *
 * Backend handlers: `services/christo/app/handlers/catalyst/demo.ts`.
 */
import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

export interface ResetDemoResponse {
  success: true;
  enterpriseId: string;
  resetAt: string;
}

export interface BootstrapDemoResponse {
  success: true;
  enterpriseId: string;
  alreadyExisted: boolean;
  completedAt: string;
}

export interface UpdateDemoBrandNameResponse {
  success: true;
  enterpriseId: string;
  name: string;
  updatedAt: string;
}

export async function resetDemoEnvironment(): Promise<ResetDemoResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/reset`, {
    method: "POST",
  })) as ResetDemoResponse;
}

export async function bootstrapDemoEnvironment(): Promise<BootstrapDemoResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/bootstrap`, {
    method: "POST",
  })) as BootstrapDemoResponse;
}

/**
 * Rebrand the demo tenant. Pass an empty string (or call
 * `resetDemoBrandName`) to revert to the seeded default ("Vitalora
 * Consumer Brands"). The backend rejects values longer than 80 chars and
 * any value containing an em-dash.
 */
export async function updateDemoBrandName(
  name: string,
): Promise<UpdateDemoBrandNameResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/brand`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  })) as UpdateDemoBrandNameResponse;
}

/** Reset the demo brand name to the seeded default. */
export async function resetDemoBrandName(): Promise<UpdateDemoBrandNameResponse> {
  return updateDemoBrandName("");
}
